/**
 * Quran Service
 * Handles all Quran-related API calls (Sora, Aya, Evaluation)
 */

import { fetchAPI, API_BASE_URL } from './apiClient';

interface Sora {
  id: number;
  number: number;
  ar: string;
  en: string;
  ayat_count: number;
  [key: string]: any;
}

interface Aya {
  id: number;
  number: number;
  text: string;
  meaning: string;
  transliteration: string;
  segments: number[][];
  sora: number;
  [key: string]: any;
}

interface EvaluateResponse {
  ratios: number[];
  misPos: number[][];
  startIndex: number;
  endIndex: number;
  messages: string[];
  attempt_ids: number[];
}

interface Restriction {
  id: number;
  user: number;
  from_aya: number;
  to_aya: number;
  username: string;
  from_sora_number: number;
  from_sora_ar: string;
  from_aya_number: number;
  to_sora_number: number;
  to_sora_ar: string;
  to_aya_number: number;
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface VersionInfo {
  version: string;
  min_supported_version: string;
  latest_version: string;
  force_update: boolean | null;
  update_available: boolean | null;
  update_url?: string;
  message?: string;
}

export const quranService = {
  /**
   * Get version info. Pass clientVersion to get force_update/update_available fields.
   */
  async getVersion(clientVersion?: string): Promise<VersionInfo> {
    const path = clientVersion
      ? `/api/version/?client_version=${encodeURIComponent(clientVersion)}`
      : '/api/version/';
    const url = `${API_BASE_URL}${path}`;
    // Use raw fetch — BE may return non-2xx (e.g. 426) with the update payload in the body.
    // fetchAPI would throw before we can read force_update/update_available.
    const response = await fetch(url);
    return response.json();
  },

  /**
   * Get all Soras
   */
  async getSorat(pageSize: number = 200): Promise<Sora[]> {
    const response = await fetchAPI<PaginatedResponse<Sora>>(`/api/sora/?page_size=${pageSize}`);
    return response.results;
  },

  /**
   * Get specific Aya by Sora number and Aya number
   */
  async getAya(soraNumber: number, ayaNumber: number): Promise<Aya | null> {
    const response = await fetchAPI<PaginatedResponse<Aya>>(
      `/api/aya/?sora__number=${soraNumber}&number=${ayaNumber}`
    );
    return response.results && response.results.length > 0 ? response.results[0] : null;
  },

  /**
   * Get all Ayas of a Sora
   */
  async getAyasBySora(soraNumber: number, pageSize: number = 500): Promise<Aya[]> {
    const response = await fetchAPI<PaginatedResponse<Aya>>(
      `/api/aya/?sora__number=${soraNumber}&page_size=${pageSize}`
    );
    return response.results;
  },

  /**
   * Evaluate pronunciation
   */
  async evaluate(audioBlob: Blob, ayaId: number): Promise<EvaluateResponse> {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'rec.wav');
    formData.append('aya_id', ayaId.toString());

    // Standard multipart/form-data. fetch handles the boundary if we don't set Content-Type manually.
    return fetchAPI<EvaluateResponse>('/api/evaluate/', {
      method: 'POST',
      body: formData,
      // We need to tell fetchAPI NOT to set Content-Type to application/json
      headers: {
        // Leaving Content-Type undefined allows the browser to set it with the correct boundary
      } as any,
    });
  },

  /**
   * Get user restrictions
   */
  async getRestrictions(): Promise<Restriction[]> {
    const response = await fetchAPI<PaginatedResponse<Restriction>>('/api/restriction/');
    return response.results;
  }
};
